<script setup lang="ts">
import { ref, watch, computed, onUnmounted } from 'vue'
import { useSnackbar,SnackbarMessages } from 'vue3-snackbar'

interface SnackbarMessage {
  type: 'success' | 'error'
  text: string
  id: number
}

const props = defineProps<{
  successMessage?: string | null
  errorMessage?: string | null
  successMessageKey?: number
  errorMessageKey?: number
  duration?: number
}>()

const snackbar = useSnackbar()
const activeSnackbars = ref<SnackbarMessage[]>([])
const MAX_SNACKBARS = 3
const DURATION = computed(() => props.duration ? props.duration : 5000)
let messageId = 0

const showSnackbar = (type: 'success' | 'error', text: string) => {
  const id = messageId++
  const message = { type, text, id }

  if (SnackbarMessages.value.length >= MAX_SNACKBARS) {
    SnackbarMessages.value.shift() // Remove the oldest snackbar from the queue
    activeSnackbars.value.shift() // Remove the oldest snackbar from the active list
  }

  activeSnackbars.value.push(message)
  snackbar.add({
    type: message.type,
    text: message.text,
    duration: DURATION.value,
    onClose: () => {
      activeSnackbars.value = activeSnackbars.value.filter(m => m.id !== id)
    }
  })
}

watch(
  () => [props.successMessage, props.successMessageKey],
  ([newMessage]) => {
    if (newMessage) {
      showSnackbar('success', newMessage)
    }
  },
  { immediate: true }
)

watch(
  () => [props.errorMessage, props.errorMessageKey],
  ([newMessage]) => {
    if (newMessage) {
      showSnackbar('error', newMessage)
    }
  },
  { immediate: true }
)

const clearAll = () => {
  snackbar.clear()
  activeSnackbars.value = []
}

onUnmounted(() => {
  snackbar.clear()
  activeSnackbars.value = []
})

defineExpose({
  showSnackbar,
  clearAll
})
</script>

<template>
  <slot :duration="DURATION" />
</template>
